import React, { useState } from 'react';

import { notify } from '@NOTIFICATION/Notificator';

import { EntityTable, EntityFormModal } from '@COMPONENTS/SHARED/EntityManagement';

import {
    useGetAllBrands,
    useCreateBrand,
    useUpdateBrand,
    useDeleteBrand,
    useGetAllPropertiesAdmin,
} from '@SERVICES';
import { Brand } from '@INTERFACES/api/brands';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function BrandsTab() {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingBrand, setEditingBrand] = useState<Brand | null>(null);

    const { data: brands = [], isLoading, refetch } = useGetAllBrands();
    const { data: contractTypes = [], isLoading: isLoadingContractTypes } = useGetAllPropertiesAdmin();
    const [createBrand, { isLoading: isCreating }] = useCreateBrand();
    const [updateBrand, { isLoading: isUpdating }] = useUpdateBrand();
    const [deleteBrand] = useDeleteBrand();

    const columns = [
        {
            key: 'id' as keyof Brand,
            header: 'ID',
        },
        {
            key: 'name' as keyof Brand,
            header: 'Name',
        },
        {
            key: 'category' as keyof Brand,
            header: 'Category',
            render: (value: string | null) => value || '-',
        },
        {
            key: 'contract_types' as keyof Brand,
            header: 'Contract Types',
            render: (value: number | null) => {
                if (!value) return '-';
                const contractType = contractTypes.find((ct) => ct.id === value);
                return contractType ? contractType.name : `ID: ${value}`;
            },
        },
        {
            key: 'logo_svg' as keyof Brand,
            header: 'Logo',
            render: (value: string | null) => (
                value ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                            src={value}
                            alt="Brand logo"
                            style={{
                                width: '24px',
                                height: '24px',
                                objectFit: 'contain',
                            }}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                        <span>Logo</span>
                    </div>
                ) : (
                    'No Logo'
                )
            ),
        },
    ];

    const formFields = [
        {
            key: 'name',
            label: 'Name',
            type: 'text' as const,
            required: true,
            placeholder: 'Enter brand name',
        },
        {
            key: 'category',
            label: 'Category',
            type: 'text' as const,
            placeholder: 'Enter category (optional)',
        },
        {
            key: 'contract_types',
            label: 'Contract Types',
            type: 'select' as const,
            placeholder: 'Select contract type (optional)',
            options: contractTypes.map((contractType) => ({
                value: contractType.id,
                label: contractType.name,
            })),
        },
        {
            key: 'logo_svg',
            label: 'Logo SVG',
            type: 'file' as const,
            placeholder: 'Upload SVG logo file (optional)',
        },
    ];

    const handleCreate = () => {
        setEditingBrand(null);
        setIsFormModalOpen(true);
    };

    const handleEdit = (brand: Brand) => {
        setEditingBrand(brand);
        setIsFormModalOpen(true);
    };

    const handleDelete = async (brand: Brand) => {
        if (window.confirm(`Are you sure you want to delete "${brand.name}"?`)) {
            try {
                await deleteBrand(brand.id).unwrap();
                notify.success('Brand deleted successfully!');
                refetch();
            } catch (error) {
                // Extract specific error message from API response
                let errorMessage = 'Failed to delete brand!';
                
                if (error && typeof error === 'object' && 'status' in error) {
                    const fetchError = error as FetchBaseQueryError;
                    if (typeof fetchError.status === 'number' && fetchError.data) {
                        // Try to extract the message from the API response
                        const errorData = fetchError.data as any;
                        if (errorData && typeof errorData === 'object' && errorData.message) {
                            errorMessage = errorData.message;
                        }
                    }
                }
                
                notify.error(errorMessage);
            }
        }
    };

    const handleFormSubmit = async (formData: any) => {
        try {
            if (editingBrand) {
                await updateBrand({ id: editingBrand.id, ...formData }).unwrap();
                notify.success('Brand updated successfully!');
            } else {
                await createBrand(formData).unwrap();
                notify.success('Brand created successfully!');
            }
            setIsFormModalOpen(false);
            setEditingBrand(null);
            refetch();
        } catch (error) {
            notify.error(`Failed to ${editingBrand ? 'update' : 'create'} brand!`);
        }
    };

    const handleFormClose = () => {
        setIsFormModalOpen(false);
        setEditingBrand(null);
    };

    return (
        <div style={{ padding: '24px' }}>
            <EntityTable
                data={brands}
                columns={columns}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={handleCreate}
                isLoading={isLoading}
                entityName="Brand"
            />

            <EntityFormModal
                isOpen={isFormModalOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                title={editingBrand ? 'Edit Brand' : 'Create Brand'}
                fields={formFields}
                initialData={editingBrand}
                isLoading={isCreating || isUpdating}
            />
        </div>
    );
}

export default BrandsTab;
